import { useState, useEffect, useMemo } from "react"
import PropTypes from "prop-types"
import { bindActionCreators } from "redux"
import { useMatches } from "react-router-dom"
import moment from "moment"
import { connect } from "react-redux"
import { get, chain, isEmpty } from "lodash"
import { Alert } from "@mui/material"
import { ContentCopy, Close, ThumbUp, ThumbUpOffAlt, ThumbDown, ThumbDownOffAlt } from "@mui/icons-material"
import { Creators as NotificationCreators } from "modules/notifications/model"
import Notifications from "modules/notifications"
import { track } from "helpers/analytics"
import FeedbackForm from "components/feedback-form"
import useLocalStorageSummaries from "../hooks/useLocalSummaries"
import { TRACKING_EVENTS } from "../constants"
import { copySummary } from "../view"
import Summary from "./summary"
import Header from "./header"

import "../styles.scss"

function AISummaryPopout({ addNotification }) {
  const matches = useMatches()
  const summaryKey = chain(matches)
    .first()
    .get("params.summaryKey")
    .value()

  const [summaries, setLocalStorageSummary, localStorageError] = useLocalStorageSummaries()
  const [thumb, setThumb] = useState(null)
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const localStorageSummary = useMemo(() => summaries?.[summaryKey], [summaries, summaryKey])

  const { summary, timestamp, meta, isGeneratedByAI } = localStorageSummary ?? {}
  const { resource, model, base, startDate, endDate } = meta ?? {}
  const { name: resourceName } = resource ?? {}

  const feedbackTrackingProps = { direction: thumb, ...meta }
  useEffect(() => {
    const { feedback } = localStorageSummary || {}
    setThumb(feedback)
  }, [localStorageSummary])

  const thumbsClick = (direction) => () => {
    if (thumb === direction) {
      return
    }
    track(TRACKING_EVENTS.FEEDBACK, feedbackTrackingProps)
    setFeedbackOpen(true)
    if (!localStorageError) {
      setLocalStorageSummary(summaryKey, { ...localStorageSummary, feedback: direction })
    }
  }

  // Backward compatibility for old summaries
  const adjustedModel = isEmpty(model) ? { id: get(meta, "modelId") } : model

  const headerButtons = [
    {
      id: "close",
      title: "Close window",
      disabled: false,
      onClick: () => window.close(),
      Icon: Close,
    },
  ]

  const summaryButtons = [
    {
      id: "thumbsUp",
      title: "Helpful summary",
      onClick: thumbsClick("positive"),
      Icon: thumb === "positive" ? ThumbUp : ThumbUpOffAlt,
    }, {
      id: "thumbsDown",
      title: "Unhelpful summary",
      onClick: thumbsClick("negative"),
      Icon: thumb === "negative" ? ThumbDown : ThumbDownOffAlt,
    },
    {
      id: "copy",
      title: "Copy to clipboard",
      disabled: false,
      onClick: () => {
        track(TRACKING_EVENTS.COPY, { resource, popout: true })
        copySummary(summary, resourceName, addNotification)
      },
      Icon: ContentCopy,
    },
  ]

  return <div className="components_ai-summary-popout">
    <Header {...{ headerButtons, timestamp, resource, model: adjustedModel, startDate, endDate, base, showDateRange: isGeneratedByAI }} />
    {summary
      ? <Summary {...{ isGenerated: !!summary, summary, timestamp: moment(timestamp, "x"), error: null, headerButtons, summaryButtons, startDate, endDate, model: adjustedModel, base, summaryKey, showDateRange: isGeneratedByAI }} />
      : <div className="content">
        <Alert severity="warning">
          <b>Generated summary not found</b>
          <p>Sorry for the inconvenience. Please try closing this window and generating the summary again.</p>
        </Alert>
      </div>}
    <FeedbackForm
      open={feedbackOpen}
      onClose={() => setFeedbackOpen(false)}
      feedbackTrackingProps={feedbackTrackingProps}
      trackingEvent={TRACKING_EVENTS.ADDITIONAL_FEEDBACK}
      getPlaceholderText={(direction) => `Why was this summary ${direction === "positive" ? "helpful" : "unhelpful"}?`}
    />
    <Notifications />
  </div>
}

AISummaryPopout.propTypes = {
  addNotification: PropTypes.func.isRequired,
}

export default connect(null, (dispatch) => bindActionCreators({
  addNotification: NotificationCreators.add,
}, dispatch))(AISummaryPopout)
